import { useCallback, useEffect, useState } from 'react';

/**
 * Keeps the side-rail preference local to the browser session — whether the
 * rail is open, and which panel was last shown. The shell consumes a small
 * state boundary and does not reach into storage while it renders its layout.
 *
 * Two keys, not one object, so the pre-registry preference keeps working: an
 * existing `workbench.commentPanel.<taskId>` value still decides open/closed,
 * and a session that predates the panel registry simply has no panel
 * preference and falls back to the first available panel.
 */
const OPEN_KEY_PREFIX = 'workbench.commentPanel.';
const PANEL_KEY_PREFIX = 'workbench.taskPanel.';

function readSession(key: string | null): string | null {
  if (!key || typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Session preference is non-critical and may be unavailable in a
    // privacy-restricted browser context.
    return null;
  }
}

function writeSession(key: string | null, value: string): void {
  if (!key || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // QuotaExceededError — panel state is non-critical.
  }
}

export interface TaskPanelPreference {
  open: boolean;
  setOpen: (next: boolean) => void;
  /** Last panel the user selected, or null when they never have. */
  panelId: string | null;
  setPanelId: (next: string) => void;
}

export function useTaskPanelState(taskId: string | null | undefined): TaskPanelPreference {
  const openKey = taskId ? `${OPEN_KEY_PREFIX}${taskId}` : null;
  const panelKey = taskId ? `${PANEL_KEY_PREFIX}${taskId}` : null;
  const [open, setOpenState] = useState(true);
  const [panelId, setPanelIdState] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized || typeof window === 'undefined') return;
    setOpenState(readSession(openKey) !== '0');
    setPanelIdState(readSession(panelKey));
    setInitialized(true);
  }, [initialized, openKey, panelKey]);

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    writeSession(openKey, next ? '1' : '0');
  }, [openKey]);

  const setPanelId = useCallback((next: string) => {
    setPanelIdState(next);
    writeSession(panelKey, next);
  }, [panelKey]);

  return { open, setOpen, panelId, setPanelId };
}

/**
 * Open/closed only. Retained because the shell's existing tests and the
 * embedded Browse Tasks path read the boolean pair directly.
 */
export function useCommentPanelState(taskId: string | null | undefined) {
  const { open, setOpen } = useTaskPanelState(taskId);
  return [open, setOpen] as const;
}
